import {
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { fetchNoShowAppointments } from "../../utils/APIHelper";
import { getContextAwareToken } from "../../utils/storageHelper";
import { ReusableDataGrid } from "../shared/ReusableDataGrid";

interface Patient {
  id: number;
  avatar: string;
  name: string;
  lastAppointment: string;
  time: string;
  contact: string;
  visitType: string;
  reason: string;
  diagnosis: string;
  status: string;
  email: string;
  appointmentId: string;
  noshow: string;
  cancelReason?: string;
  visitMode?: string;
  patientId?: string;
  providerId?: string;
  insurance?: string;
  dob?: string;
}

// Removed unused Provider and Transition (menu/dialog not used in this table)

export default function NoShowTable() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState<string>("all");

  useEffect(() => {
    async function fetchPatients() {
      setLoading(true);
      try {
        // Get token from auth context with role awareness
        const token = getContextAwareToken(user?.token, user?.role) || "";
        console.log("Token available:", !!token);

        if (!token) {
          console.error("No token found");
          toast.error("Authentication token not found");
          setLoading(false);
          return;
        }

        // Get provider ID from user context
        const providerId = user?.id;
        console.log("Provider ID:", providerId);

        if (!providerId) {
          console.error("No provider ID found");
          toast.error("Provider information not found");
          setLoading(false);
          return;
        }

        console.log("Fetching no-show appointments for provider:", providerId);

        const res = await fetchNoShowAppointments(token, String(providerId));

        console.log("Fetched no-show data:", res);

        // Extract the data array from response
        const noShowData = res?.data || [];
        console.log("No-show appointments received:", noShowData.length);

        // Log first record to see structure
        if (noShowData.length > 0) {
          console.log("First no-show record structure:", noShowData[0]);
          console.log("Patient data in first record:", noShowData[0]?.patient);
          console.log(
            "Patient.patient in first record:",
            noShowData[0]?.patient?.patient
          );
          console.log(
            "Appointment data in first record:",
            noShowData[0]?.appointment
          );
          console.log(
            "Insurance in appointment:",
            noShowData[0]?.appointment?.insurance
          );
        }

        if (!noShowData || noShowData.length === 0) {
          console.log("No no-show appointments found");
          setPatients([]);
          setLoading(false);
          return;
        }

        // Deduplicate by appointment_id - keep only the first record for each appointment
        // The backend creates 2 records per cancelled appointment (for 30s and 60s follow-up messages)
        // But we only want to show 1 entry per appointment in the table
        const uniqueAppointments = noShowData.reduce(
          (acc: any[], record: any) => {
            const appointmentId = record.appointment_id;
            const existingRecord = acc.find(
              (r: any) => r.appointment_id === appointmentId
            );

            if (!existingRecord) {
              acc.push(record);
            }

            return acc;
          },
          []
        );

        console.log(
          "After deduplication:",
          uniqueAppointments.length,
          "unique appointments"
        );

        const mapped = uniqueAppointments.map((record: any, idx: number) => {
          // Parse appointment date
          const appointmentDate = new Date(
            record.appointment?.appointment_date
          );
          const formattedDate = `${(appointmentDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}/${appointmentDate
            .getDate()
            .toString()
            .padStart(2, "0")}/${appointmentDate.getFullYear()}`;

          // Extract patient name
          const firstName = record.patient?.patient?.first_name || "";
          const lastName = record.patient?.patient?.last_name || "";
          const patientName =
            `${firstName} ${lastName}`.trim() || "Unknown Patient";

          // Extract patient contact
          const patientContact =
            record.patient?.patient?.contact_number || "N/A";

          // Extract patient email - it's at record.patient.email, not record.patient.patient.email
          const patientEmail = record.patient?.email || "";

          // Extract and format DOB
          const dobRaw =
            record.patient?.patient?.birth_date ||
            record.patient?.patient?.date_of_birth;
          const formattedDOB = dobRaw
            ? dayjs(dobRaw).format("MM/DD/YYYY")
            : "N/A";

          // Extract insurance from appointment
          const patientInsurance = record.appointment?.insurance || "N/A";

          console.log("Mapping no-show record:", {
            id: record.id,
            name: patientName,
            appointmentId: record.appointment_id,
            date: formattedDate,
            time: record.appointment?.appointment_time?.substring(0, 5),
            email: patientEmail,
            contact: patientContact,
            dob: formattedDOB,
            insurance: patientInsurance,
            patientData: record.patient?.patient,
            patientUserData: record.patient, // This has the email
            appointmentData: record.appointment,
          });

          const formattedVisitType = record.appointment?.visit_type
            ? record.appointment.visit_type
                .split("_")
                .map(
                  (word: string) => word.charAt(0) + word.slice(1).toLowerCase()
                )
                .join(" ")
            : "In Person";

          return {
            id: idx + 1,
            avatar:
              "https://upload.wikimedia.org/wikipedia/commons/9/99/Sample_User_Icon.png",
            name: patientName,
            lastAppointment: formattedDate,
            time:
              record.appointment?.appointment_time?.substring(0, 5) || "N/A",
            contact: String(patientContact),
            visitType: formattedVisitType,
            reason: record.appointment?.reason_for_visit || "Not specified",
            diagnosis: record.appointment?.diagnosis || "Not provided",
            status: "Canceled",
            email: patientEmail,
            appointmentId: record.appointment_id || "",
            noshow: record.cancellation_date
              ? dayjs(record.cancellation_date).format("MM/DD/YYYY HH:mm")
              : "N/A",
            visitMode: formattedVisitType,
            cancelReason: record.reason_cancellation || "Not provided",
            patientId: record.patient_id,
            providerId: record.provider_id,
            dob: formattedDOB,
            insurance: patientInsurance,
          } as Patient;
        });

        console.log("Mapped no-show patients:", mapped);
        setPatients(mapped);
      } catch (e) {
        console.error("Error fetching no-show appointments:", e);
        if (e instanceof Error) {
          console.error("Error details:", {
            message: e.message,
            stack: e.stack,
          });
        }
        toast.error("Failed to load no-show appointments.");
      } finally {
        setLoading(false);
      }
    }
    fetchPatients();
  }, [user]);

  const filterPatientsByDate = (list: Patient[]): Patient[] => {
    if (filterDate === "all") return list;
    const today = new Date();
    const parsed = (s: string) => {
      const [d, m, y] = s.split("-");
      return new Date(`${y} -${m} -${d} `);
    };
    return list.filter((p) => {
      if (!p.lastAppointment) return false;
      const ap = parsed(p.lastAppointment);
      if (filterDate === "today")
        return ap.toDateString() === today.toDateString();
      if (filterDate === "tomorrow") {
        const t = new Date(today);
        t.setDate(today.getDate() + 1);
        return ap.toDateString() === t.toDateString();
      }
      if (filterDate === "7days") {
        const end = new Date(today);
        end.setDate(today.getDate() + 7);
        return ap >= today && ap <= end;
      }
      return true;
    });
  };

  const filtered = filterPatientsByDate(patients);

  const handleRowClick = (params: GridRowParams<Patient>) => {
    console.log("Row clicked:", params.row);
    console.log(`/layout/noshow/${params.row.appointmentId}`);

    navigate(`/layout/noshow/${params.row.appointmentId}`, {
      state: { patient: params.row },
    });
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Sr. No.",
      width: 90,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          sx={{ fontSize: "14px", color: "#2D3748", fontWeight: 500 }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "PATIENT NAME",
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box
            component="img"
            src={params.row.avatar}
            alt="avatar"
            sx={{ width: 40, height: 40, borderRadius: "50%" }}
          />
          <Typography
            sx={{ fontSize: "14px", color: "#2D3748", fontWeight: 600 }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "lastAppointment",
      headerName: "DATE & TIME",
      width: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#2D3748",
              fontWeight: 500,
              lineHeight: "1.4",
            }}
          >
            {params.value}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#A0AEC0",
              fontWeight: 400,
              lineHeight: "1.4",
            }}
          >
            {params.row.time}
          </Typography>
        </Box>
      ),
    },
    {
      field: "reason",
      headerName: "APPOINTMENT REASON",
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          sx={{ fontSize: "14px", color: "#2D3748", fontWeight: 500 }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "visitType",
      headerName: "VISIT TYPE",
      width: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          sx={{ fontSize: "14px", color: "#2D3748", fontWeight: 500 }}
        >
          {params.value || "In Person"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "STATUS",
      width: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (params.value === "Canceled" || params.value === "Missed") {
          return (
            <span
              style={{
                backgroundColor: "#FED7D7",
                color: "#E53E3E",
                fontSize: "12px",
                padding: "0px 8px",
                borderRadius: "20px",
              }}
            >
              {params.value}
            </span>
          );
        }

        if (params.value === "Rebooked") {
          return (
            <span
              style={{
                backgroundColor: "#C6F6D5",
                color: "#22543D",
                fontSize: "12px",
                padding: "2px 8px",
                borderRadius: "20px",
              }}
            >
              {params.value}
            </span>
          );
        }

        return <span>{params.value}</span>;
      },
    },
  ];

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <>
      {/* Table Header with Title and Filter */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            padding: "20px 24px",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#2D3748",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            Recent Recovery Activity
          </Typography>

          <FormControl sx={{ minWidth: 180 }}>
            <Select
              value={filterDate}
              onChange={(e: SelectChangeEvent) => setFilterDate(e.target.value)}
              displayEmpty
              sx={{
                fontSize: "14px",
                fontFamily: "Quicksand, sans-serif",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E2E8F0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#CBD5E0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4299E1",
                },
                borderRadius: "8px",
                height: "40px",
              }}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="tomorrow">Tomorrow</MenuItem>
              <MenuItem value="7days">Next 7 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table or Empty State */}
        {filtered.length === 0 ? (
          <Box
            sx={{
              padding: "60px 20px",
              textAlign: "center",
              backgroundColor: "#F7FAFC",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                color: "#718096",
                fontFamily: "Quicksand, sans-serif",
                marginBottom: "8px",
              }}
            >
              No canceled appointments found
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#A0AEC0",
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              {filterDate !== "all"
                ? "Try adjusting your filter to see more results"
                : "Canceled appointments will appear here"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            <ReusableDataGrid
              columns={columns}
              data={filtered}
              onRowClick={handleRowClick}
              hideCheckboxSelection={true}
              hideFilters={true}
              sx={{
                border: "none",
                fontFamily: "Quicksand, sans-serif",
                "& .MuiDataGrid-root": {
                  border: "none",
                },
                "& .MuiDataGrid-main": {
                  border: "none",
                },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  minHeight: "72px !important",
                  maxHeight: "72px !important",
                  "&:hover": {
                    backgroundColor: "#F7FAFC",
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #E2E8F0",
                  fontSize: "14px",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  minHeight: "72px !important",
                  maxHeight: "72px !important",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#FAFAFA",
                  borderBottom: "1px solid #E2E8F0",
                  minHeight: "48px !important",
                  maxHeight: "48px !important",
                },
                "& .MuiDataGrid-columnHeader": {
                  padding: "12px 16px",
                  minHeight: "48px !important",
                  maxHeight: "48px !important",
                  "&:focus": {
                    outline: "none",
                  },
                  "&:focus-within": {
                    outline: "none",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 600,
                  fontSize: "11px",
                  color: "#718096",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontFamily: "Quicksand, sans-serif",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid #E2E8F0",
                  backgroundColor: "#FAFAFA",
                  minHeight: "52px",
                },
                "& .MuiDataGrid-virtualScroller": {
                  marginTop: "48px !important",
                },
              }}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
